import type { CryptoCurrencyDataSource, FiatCurrency } from '@0xtorch/core'
/**
 * Portfolio 全体の損益計算を実行し、会計期間ごとの計算と期末 asset value 更新を順に同期する。
 * optional profiler を渡すと主要フェーズと DB I/O の所要時間を収集する。
 */
import type {
  calculateTransactionPlByWacb,
  createAssetBalancesForWacb,
  createTransactions,
} from '@pkg/accounting'
import { createAccountingPeriods } from '@pkg/basic'
import {
  getOldestActionTimestamp,
  selectAccountingPeriodList,
  selectPortfolio,
  updatePortfolio,
  upsertAccountingPeriodList,
} from '@pkg/database-portfolio'
import { generateAccountingTransactionListOfAccountingPeriod } from './generateAccountingTransactionListOfAccountingPeriod'
import {
  type AccountingProfiler,
  noopAccountingProfiler,
} from './profiling/accountingProfiler'
import { saveAssetValues } from './saveAssetValues'
import type {
  OnUpdateCalculationProgressCallback,
  RunToDatabase,
} from './types'

type GenerateAccountingTransactionListParameters = {
  readFromDatabase: RunToDatabase
  writeToDatabase: RunToDatabase
  fiats: readonly FiatCurrency[]
  cryptoCurrencyDataSource: CryptoCurrencyDataSource
  createTransactions: (
    parameters: Parameters<typeof createTransactions>['0'],
  ) => Promise<ReturnType<typeof createTransactions>>
  createAssetBalancesForWacb: (
    parameters: Parameters<typeof createAssetBalancesForWacb>['0'],
  ) => Promise<ReturnType<typeof createAssetBalancesForWacb>>
  calculateTransactionPlByWacb: (
    parameters: Parameters<typeof calculateTransactionPlByWacb>['0'],
  ) => Promise<ReturnType<typeof calculateTransactionPlByWacb>>
  onUpdateProgress?: OnUpdateCalculationProgressCallback
  profiler?: AccountingProfiler
  maxOnceItemCount?: number
  transactionFlushItemCount?: number
}

/**
 * Portfolio のアクション履歴を元に取引と損益を計算し、最新状態へ同期する。
 * @param params DB wrapper、依存サービス、optional profiler を受け取る。
 */
export const generateAccountingTransactionList = async ({
  readFromDatabase,
  writeToDatabase,
  fiats,
  cryptoCurrencyDataSource,
  createTransactions,
  createAssetBalancesForWacb,
  calculateTransactionPlByWacb,
  onUpdateProgress,
  profiler,
  maxOnceItemCount,
  transactionFlushItemCount,
}: GenerateAccountingTransactionListParameters): Promise<void> => {
  const effectiveProfiler = profiler ?? noopAccountingProfiler
  const profiledReadFromDatabase: RunToDatabase = async (run) =>
    await effectiveProfiler.measure(
      'db.read.total',
      async () => await readFromDatabase(run),
    )
  const profiledWriteToDatabase: RunToDatabase = async (run) =>
    await effectiveProfiler.measure(
      'db.write.total',
      async () => await writeToDatabase(run),
    )

  await effectiveProfiler.measure('generate.total', async () => {
    const portfolio = await effectiveProfiler.measure(
      'generate.selectPortfolio',
      async () =>
        await profiledReadFromDatabase((database) =>
          selectPortfolio({ database }),
        ),
    )
    if (portfolio === undefined) {
      return
    }
    const fiatCurrency = fiats.find(({ id }) => portfolio.fiat === id)
    if (fiatCurrency === undefined) {
      return
    }

    if (portfolio.isTxGenerated) {
      return
    }

    const currentAccountingPeriodList = await effectiveProfiler.measure(
      'generate.selectAccountingPeriodList',
      async () =>
        await profiledReadFromDatabase((database) =>
          selectAccountingPeriodList({ database }),
        ),
    )
    const oldestActionTimestamp = await effectiveProfiler.measure(
      'generate.getOldestActionTimestamp',
      async () =>
        await profiledReadFromDatabase((database) =>
          getOldestActionTimestamp({ database }),
        ),
    )

    if (oldestActionTimestamp === undefined) {
      await effectiveProfiler.measure(
        'generate.updatePortfolio.isTxGenerated',
        async () =>
          await profiledWriteToDatabase((database) =>
            updatePortfolio({
              database,
              updating: {
                isTxGenerated: true,
              },
            }),
          ),
      )
      return
    }

    const accountingPeriodList = effectiveProfiler.measureSync(
      'generate.createAccountingPeriods',
      () =>
        createAccountingPeriods({
          oldestTimestamp:
            portfolio.startTime?.getTime() ?? oldestActionTimestamp,
          startMonth: portfolio.startMonth,
          timezone: portfolio.timezone,
        }),
      {
        currentAccountingPeriodCount: currentAccountingPeriodList.length,
      },
    )
    const unsettledAccountingPeriodList = accountingPeriodList.filter(
      (period) =>
        portfolio.closedAccountingPeriodId === null ||
        period.id > portfolio.closedAccountingPeriodId,
    )

    await effectiveProfiler.measure(
      'generate.upsertAccountingPeriodList',
      async () =>
        await profiledWriteToDatabase((database) =>
          upsertAccountingPeriodList({
            database,
            data: accountingPeriodList.map((item) => ({
              id: item.id,
              start: new Date(item.start),
              end: new Date(item.end),
            })),
          }),
        ),
      {
        periodCount: accountingPeriodList.length,
        unsettledPeriodCount: unsettledAccountingPeriodList.length,
      },
    )

    if (onUpdateProgress) {
      onUpdateProgress({
        size: unsettledAccountingPeriodList.length,
        done: 0,
        synced: 0,
      })
    }

    let done = 0
    for (const accountingPeriod of unsettledAccountingPeriodList) {
      await effectiveProfiler.measure(
        'generate.period.total',
        async () =>
          await generateAccountingTransactionListOfAccountingPeriod({
            readFromDatabase,
            writeToDatabase,
            portfolio,
            accountingPeriod,
            fiats,
            createTransactions,
            createAssetBalancesForWacb,
            calculateTransactionPlByWacb,
            onUpdateProgress,
            profiler: effectiveProfiler,
            maxOnceItemCount,
            transactionFlushItemCount,
          }),
        {
          periodId: accountingPeriod.id,
          costBasis: portfolio.costBasis,
          maxOnceItemCount,
          transactionFlushItemCount,
        },
      )

      done += 1
      if (onUpdateProgress) {
        onUpdateProgress({
          done,
          synced: 0,
        })
      }
    }

    for (const accountingPeriod of unsettledAccountingPeriodList) {
      await effectiveProfiler.measure(
        'generate.saveAssetValues.total',
        async () =>
          await saveAssetValues({
            readFromDatabase,
            writeToDatabase,
            accountingPeriod,
            cryptoCurrencyDataSource,
            fiat: fiatCurrency,
          }),
        {
          periodId: accountingPeriod.id,
        },
      )
    }

    await effectiveProfiler.measure(
      'generate.updatePortfolio.isTxGenerated',
      async () =>
        await profiledWriteToDatabase((database) =>
          updatePortfolio({
            database,
            updating: {
              isTxGenerated: true,
            },
          }),
        ),
    )
  })
}
